import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { DataFilters } from "../components/DataFilters";
import {
  eventAudienceLabel,
  eventHiddenFromFamilies,
  eventMatchesAgeFilter,
  eventMatchesSchoolFilter,
  staff,
  type AgeFilter,
  type LiftEvent,
  type School,
} from "../domain/data";
import { useEvents } from "../state/events";
import { PrototypeBanner, StaffNav } from "../components/ui";

function EventRow({
  event,
  action,
}: {
  event: LiftEvent;
  action: "attendance" | "scan" | "past";
}) {
  const { countsFor } = useEvents();
  const counts = event.kind === "rsvp" ? countsFor(event.id) : null;
  return (
    <li>
      <div className="row">
        <Link
          to={
            action === "attendance"
              ? `/staff/events/${event.id}?tab=attendance`
              : `/staff/events/${event.id}`
          }
        >
          <div className="title">
            <strong>{event.name}</strong>
          </div>
          <div className="anno">
            {event.dateLabel} · {event.timeLabel} · {event.location}
          </div>
          <div className="anno">{eventAudienceLabel(event)}</div>
          {counts ? (
            <div className="anno">
              {counts.registered} / {event.capacity} registered
              {counts.waitlisted > 0
                ? ` · ${counts.waitlisted} waiting`
                : counts.remaining === 0
                  ? " · full"
                  : ""}
            </div>
          ) : null}
        </Link>
        {action === "past" ? (
          <div className="tag muted">Past</div>
        ) : (
          <Link
            className="btn"
            to={
              action === "attendance"
                ? `/staff/events/${event.id}?tab=attendance`
                : `/staff/events/${event.id}/check-in`
            }
          >
            {action === "attendance" ? "Attendance" : "Check in"}
          </Link>
        )}
      </div>
    </li>
  );
}

export function StaffEvents() {
  const { upcomingEvents, pastEvents, programEvents } = useEvents();
  const [age, setAge] = useState<AgeFilter>("all");
  const [school, setSchool] = useState<School | "all">("all");

  const upcoming = useMemo(
    () =>
      upcomingEvents.filter(
        (event) =>
          eventMatchesAgeFilter(event, age) &&
          eventMatchesSchoolFilter(event, school),
      ),
    [upcomingEvents, age, school],
  );
  const past = useMemo(
    () =>
      pastEvents.filter(
        (event) =>
          eventMatchesAgeFilter(event, age) &&
          eventMatchesSchoolFilter(event, school),
      ),
    [pastEvents, age, school],
  );
  const programs = useMemo(
    () =>
      programEvents.filter(
        (event) =>
          eventMatchesAgeFilter(event, age) &&
          eventMatchesSchoolFilter(event, school),
      ),
    [programEvents, age, school],
  );

  function clearFilters() {
    setAge("all");
    setSchool("all");
  }

  return (
    <div className="app">
      <PrototypeBanner />
      <main className="shell">
        <StaffNav />
        <p className="meta">
          Staff · {staff.given} {staff.family} · events
        </p>
        <h1>Events</h1>
        <p className="lede">
          Afterschool programs keep one event all year. Take attendance by date
          on that event. RSVP trips are the ones families see, then drop off
          after the date. Filter by middle school or high school the same way
          you do on the roster.
        </p>

        <DataFilters
          age={age}
          onAge={setAge}
          school={school}
          onSchool={setSchool}
          onClear={clearFilters}
        />

        <div className="actions" style={{ marginTop: 0, marginBottom: "1rem" }}>
          <Link className="btn" to="/staff/events/new">
            Create event
          </Link>
        </div>

        <h2 style={{ marginTop: 0 }}>Afterschool programs</h2>
        <p className="lede">
          Staff only. Families do not see these as a growing event list.
        </p>
        {programs.length === 0 ? (
          <div className="wire dashed">
            <div className="meta">No programs match these filters</div>
          </div>
        ) : (
          <ul className="list-plain">
            {programs.map((event) => (
              <EventRow key={event.id} event={event} action="attendance" />
            ))}
          </ul>
        )}

        <h2>Upcoming RSVPs</h2>
        {upcoming.length === 0 ? (
          <div className="wire dashed">
            <div className="meta">No upcoming RSVP events match these filters</div>
          </div>
        ) : (
          <ul className="list-plain">
            {upcoming.map((event) => (
              <EventRow
                key={event.id}
                event={event}
                action={eventHiddenFromFamilies(event) ? "past" : "scan"}
              />
            ))}
          </ul>
        )}

        <h2>Past / archived</h2>
        <p className="lede">
          Hidden from families after the date passes. Attendance history stays
          here.
        </p>
        {past.length === 0 ? (
          <div className="wire dashed">
            <div className="meta">No past events match these filters</div>
          </div>
        ) : (
          <ul className="list-plain">
            {past.map((event) => (
              <EventRow key={event.id} event={event} action="past" />
            ))}
          </ul>
        )}
      </main>
    </div>
  );
}
